/* KIRAKI bag: one-tap sizes, the product form, and the bag count.
   Uses Shopify's standard CartLinesUpdateEvent so Horizon's cart drawer stays in sync. */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const routes = () => window.Theme?.routes || {};
const motionOn = () => document.documentElement.classList.contains('ks-motion');

async function fetchCart() {
  const res = await fetch((routes().cart_url || '/cart') + '.js', { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('We couldn’t refresh your cart. Reload the page to see it.');
  return res.json();
}

async function addLines(source, items) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity || 1 })),
      promise: deferred.promise,
    })
  );
  try {
    const res = await fetch(routes().cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'That size couldn’t be added. Try another size.');
    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { didError: false, items: cart.items, source: 'ks', itemCount: items.length },
    });
    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/* One-tap sizes */
document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-ks-add]') : null;
  if (!button || button.disabled || button.dataset.state === 'busy') return;
  event.preventDefault();
  const wrap = button.closest('[data-ks-qa]');
  const status = wrap?.querySelector('[data-ks-qa-status]');
  const label = button.querySelector('[data-ks-add-label]');
  const original = label?.textContent;
  button.dataset.state = 'busy';
  button.setAttribute('aria-busy', 'true');
  if (status) status.textContent = 'Adding to your cart';
  try {
    await addLines(button, [{ id: Number(button.dataset.variantId), quantity: 1 }]);
    button.dataset.state = 'added';
    if (label) label.textContent = '✓';
    if (status) status.textContent = 'Added to your cart.';
    window.KS?.spring?.(button, { scale: [0.9, 1] }, { stiffness: 520, damping: 18 });
    setTimeout(() => {
      delete button.dataset.state;
      if (label && original) label.textContent = original;
      if (status) status.textContent = '';
    }, 2400);
  } catch (error) {
    delete button.dataset.state;
    if (status) status.textContent = error.message;
  } finally {
    button.removeAttribute('aria-busy');
  }
});

/* Product form */
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-ks-product-form')) return;
  event.preventDefault();
  const button = form.querySelector('[data-ks-add-main]');
  const idInput = form.querySelector('[data-ks-variant-id]');
  const status = form.querySelector('[data-ks-form-status]');
  if (!idInput?.value) {
    form.dispatchEvent(new CustomEvent('ks:need-option', { bubbles: true }));
    if (status) status.textContent = button?.dataset.labelChoose ? button.dataset.labelChoose + ' first.' : 'Choose an option first.';
    return;
  }
  if (!button || button.dataset.state === 'busy') return;
  const text = button.querySelector('[data-ks-add-text]');
  button.dataset.state = 'busy';
  button.setAttribute('aria-busy', 'true');
  if (text) text.textContent = button.dataset.labelAdding || 'Adding';
  if (status) status.textContent = '';
  try {
    await addLines(form, [{ id: Number(idInput.value), quantity: 1 }]);
    button.dataset.state = 'added';
    if (text) text.textContent = button.dataset.labelAdded || 'Added to your cart';
    if (status) status.textContent = 'Added to your cart.';
    window.KS?.spring?.(button, { scale: [0.97, 1] }, { stiffness: 480, damping: 20 });
    setTimeout(() => {
      delete button.dataset.state;
      if (text) text.textContent = button.dataset.labelAdd || 'Add to cart';
    }, 2600);
  } catch (error) {
    delete button.dataset.state;
    if (text) text.textContent = button.dataset.labelAdd || 'Add to cart';
    if (status) status.textContent = error.message;
  } finally {
    button.removeAttribute('aria-busy');
  }
});

/* Bag count */
function renderCount(count) {
  document.querySelectorAll('[data-ks-bag-count]').forEach((el) => {
    const prev = el.textContent.trim();
    el.textContent = String(count);
    el.setAttribute('aria-label', count + (count === 1 ? ' item' : ' items'));
    if (prev !== String(count) && motionOn()) window.KS?.spring?.(el, { scale: [1.45, 1] }, { stiffness: 600, damping: 14 });
  });
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(({ cart, detail }) => renderCount(cart?.totalQuantity ?? detail?.itemCount ?? 0))
    .catch(() => {});
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) fetchCart().then((cart) => renderCount(cart.item_count)).catch(() => {});
});
